"use client"

/**
 * "Get the Spire App" — always-visible install section for the homepage + contact page.
 * This is the RELIABLE install path (the auto-popup is Chrome-gated, never iOS, not
 * reliably desktop). Platform-aware: Android/desktop → one-tap Install; iOS → Share steps;
 * macOS Safari → Add to Dock; Firefox → use Chrome/Edge. Hidden once installed.
 */
import { Download, Share, Bell, Wifi, Zap } from "lucide-react"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { trackEvent } from "@/lib/analytics"
import { useInstall } from "./use-install"

export function GetAppSection() {
  const { canInstall, installed, isIos, isMacSafari, isFirefox, manualHint, promptInstall } = useInstall()
  if (installed) return null

  const onInstall = async () => {
    trackEvent("pwa_click_install_cta", { surface: "homepage_section" })
    if (canInstall) await promptInstall()
    else toast.info(manualHint, { duration: 8000 })
  }

  return (
    <section className="mx-auto my-10 max-w-5xl px-4">
      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-gradient-to-br from-[#d60500] to-[#b50400] text-white shadow-xl">
        <div className="grid items-center gap-6 p-6 sm:p-10 md:grid-cols-[auto_1fr_auto]">
          {/* White card behind the icon — the app icon is a RED tile, so without a light
              backing it disappears against this red section. */}
          <div className="flex h-16 w-16 flex-none items-center justify-center rounded-2xl bg-white p-1.5 shadow-lg md:h-20 md:w-20">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/icons/icon-192.png" alt="Spire" className="h-full w-full rounded-xl object-contain" />
          </div>
          <div className="min-w-0">
            <h2 className="text-2xl font-bold sm:text-3xl">Get the Spire app</h2>
            <p className="mt-1 text-white/85">Install Spire for instant property access, alerts, and offline browsing — free, no app store.</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4" /> Instant launch</span>
              <span className="inline-flex items-center gap-1.5"><Bell className="h-4 w-4" /> Property alerts</span>
              <span className="inline-flex items-center gap-1.5"><Wifi className="h-4 w-4" /> Works offline</span>
            </div>
          </div>
          <div className="md:text-right">
            {isIos ? (
              <div className="rounded-xl bg-white/15 px-4 py-3 text-sm">
                <span className="font-semibold">Tap <Share className="inline h-4 w-4 align-text-bottom" /> Share</span> → "Add to Home Screen"
              </div>
            ) : isMacSafari ? (
              <div className="rounded-xl bg-white/15 px-4 py-3 text-sm font-semibold">Safari → File → "Add to Dock"</div>
            ) : isFirefox ? (
              <div className="rounded-xl bg-white/15 px-4 py-3 text-sm">Open in Chrome or Edge to install</div>
            ) : (
              <Button onClick={onInstall} size="lg" className="gap-2 bg-white text-[#d60500] hover:bg-white/90">
                <Download className="h-5 w-5" /> Install app
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
